import { FEATURE_FLAGS } from '@/constants/feature-flags';
import { FORM_LABELS, SIMULATION_LABELS } from '@/constants/labels';
import { getUserName } from '@/utils';
import type { IManagedUserTabItem } from '../types';
import { useMemo } from 'react';
import type {
  IManagedUserSummary,
  IManagerProduct,
  IManagerProductOption,
} from '@/types';

// ================================
// Types
// ================================

/**
 * Input parameters accepted by the `useManagedUserMemoData` hook.
 *
 * Both fields are optional/nullable because the data is typically loaded
 * asynchronously; the hook must handle the "not-yet-fetched" state gracefully.
 */
export interface UseManagedUserMemoDataParams {
  /** The full summary record for the user whose profile is being viewed. */
  managedUser?: IManagedUserSummary | null;
  /**
   * The certification product currently selected in the dashboard filter.
   * When `null` or `undefined` all products are shown; when set, data is
   * scoped to that product's `certificationId`.
   */
  managerActiveProduct?: IManagerProduct | null;
}

/**
 * All memoized values returned by `useManagedUserMemoData`.
 *
 * Every field is derived from `managedUser` and/or `managerActiveProduct`, so
 * consumers never need to repeat the same filtering/mapping logic themselves.
 */
interface UseManagedUserMemoDataResult {
  /** The full name of the managed user, derived from managedUser.userName */
  managedUserFullName: string;
  /** All certifications (products) associated with the managed user */
  products: IManagerProduct[];
  /** Certificates filtered by managerActiveProduct if set, otherwise all certificates */
  certifications?: IManagedUserSummary['certificates'];
  /** Practice certificates filtered by managerActiveProduct if set, otherwise all practice certificates */
  practicesCertificates?: IManagedUserSummary['practiceCertificates'];
  /** Options for selecting a product in the dashboard */
  productOptions: IManagerProductOption[];
  /** Roleplays filtered by managerActiveProduct if set, otherwise all roleplays */
  roleplays?: IManagedUserSummary['roleplays'];
  /** Tabs representing different sections of the managed user dashboard */
  tabs: IManagedUserTabItem[];
}

// ================================
// Hook
// ================================

/**
 * Encapsulates all memoized derived data for the managed-user profile dashboard.
 *
 * Keeps expensive derivations out of the main feature hook and makes them
 * independently testable.
 *
 * ### Filtering strategy
 * Several fields (`certifications`, `practicesCertificates`, `roleplays`) share
 * the same pattern:
 * - If `managerActiveProduct` is set  → filter the collection to only items
 *   whose `certificationId` matches the active product.
 * - If no product is active (showing "All") → return the full, unfiltered array
 *   (falling back to `[]` when the data hasn't loaded yet).
 *
 * This keeps filtering logic co-located and prevents it from leaking into
 * components or the parent feature hook.
 */
export const useManagedUserMemoData = ({
  managedUser,
  managerActiveProduct,
}: UseManagedUserMemoDataParams): UseManagedUserMemoDataResult => {
  // ================================
  // Derived values
  // ================================

  /**
   * Human-readable full name for the managed user.
   * Re-computed only when `managedUser` changes (the reference equality check
   * on the whole object is intentional — any update to user data should
   * trigger a re-derive).
   */
  const managedUserFullName = useMemo(
    () =>
      getUserName({ name: managedUser?.userName } as Parameters<typeof getUserName>[0], 'fullName'),
    [managedUser],
  );

  /**
   * Flat list of all certification products linked to the managed user.
   * Falls back to an empty array while `managedUser` is loading so that
   * consumers can safely iterate without null-checks.
   */
  const products: IManagerProduct[] = useMemo(
    () => managedUser?.certifications || [],
    [managedUser],
  );

  /**
   * Final (graded) certification records for the managed user.
   *
   * When a product filter is active, only certificates whose `certificationId`
   * matches the selected product are included, allowing the dashboard to scope
   * all data sections to the same product simultaneously.
   */
  const certifications = useMemo(() => {
    if (managerActiveProduct) {
      return managedUser?.certificates?.filter(
        (cert) => cert.certificationId === managerActiveProduct.certificationId,
      );
    }

    // No active product filter — return everything (or an empty array as fallback).
    return managedUser?.certificates || [];
  }, [managedUser, managerActiveProduct]);

  /**
   * Practice (non-graded) certification records for the managed user.
   *
   * Applies the same product-scoping strategy as `certifications` above:
   * filter by `certificationId` when a product is selected, otherwise return all.
   */
  const practicesCertificates = useMemo(() => {
    if (managerActiveProduct) {
      return managedUser?.practiceCertificates?.filter(
        (cert) => cert.certificationId === managerActiveProduct.certificationId,
      );
    }

    // No active product filter — return everything (or an empty array as fallback).
    return managedUser?.practiceCertificates || [];
  }, [managedUser, managerActiveProduct]);

  /**
   * Role-play / simulation records for the managed user.
   *
   * Same filtering pattern as `certifications` and `practicesCertificates`:
   * scoped to the active product when one is selected.
   *
   * Note: The filter variable is named `cert` for consistency with the other
   * filters even though these records represent roleplays, not certificates.
   */
  const roleplays = useMemo(() => {
    if (managerActiveProduct) {
      return managedUser?.roleplays?.filter(
        (cert) => cert.certificationId === managerActiveProduct.certificationId,
      );
    }

    // No active product filter — return everything (or an empty array as fallback).
    return managedUser?.roleplays || [];
  }, [managedUser, managerActiveProduct]);

  /**
   * Tab configuration array for the managed-user profile dashboard.
   *
   * Each tab entry includes:
   * - `id`          — stable key used for tab switching logic
   * - `label`       — display string sourced from shared label constants
   * - `labelCount`  — badge count shown next to the tab label, derived from
   *                   the already-filtered collections above so counts stay
   *                   consistent with the active product filter
   * - `enabled`     — optional boolean; when `false` the tab is hidden/disabled
   *                   (currently used to gate the final-certification tab via a
   *                   feature flag)
   *
   * Dependencies include the filtered collections rather than `managedUser`
   * directly, so the tabs only re-render when the visible item counts change.
   */
  const tabs: IManagedUserTabItem[] = useMemo(() => {
    // Derive badge counts from the (possibly filtered) collections.
    const certCount = certifications?.length || 0;
    const pracCount = practicesCertificates?.length || 0;
    const simCount = roleplays?.length || 0;

    return [
      {
        id: 'certification',
        label: SIMULATION_LABELS.CERTIFICATION_FINAL,
        labelCount: certCount,
        // The final-certification tab is feature-flagged; it will be hidden
        // when ENABLE_FINAL_CERTIFICATION is false.
        enabled: FEATURE_FLAGS.ENABLE_FINAL_CERTIFICATION,
      },
      {
        id: 'practice',
        label: SIMULATION_LABELS.PRACTICE_CERTIFICATION,
        labelCount: pracCount,
        // Practice tab is always visible; no feature flag needed.
      },
      {
        id: 'role-play',
        label: SIMULATION_LABELS.SHARED_ROLEPLAY,
        labelCount: simCount,
        // Role-play tab is always visible; no feature flag needed.
      },
    ];
  }, [certifications, practicesCertificates, roleplays]);

  /**
   * Dropdown options for the product-filter control in the dashboard header.
   *
   * Always starts with an "All products" sentinel option (`value: 'all'`) so
   * the manager can clear the product filter, followed by one option per
   * certification product assigned to the managed user.
   *
   * Re-computed when `managedUser` changes (i.e. when the product list itself
   * changes), not when the active product selection changes.
   */
  const productOptions: IManagerProductOption[] = useMemo(() => {
    // Map each certification product to a { value, label } dropdown option.
    const mappedOptions = managedUser?.certifications?.map((product) => ({
      value: product.certificationId.toString(),
      label: product.certificationTitle,
    }));

    // Prepend the "All" option so it always appears first in the dropdown.
    return [{ value: 'all', label: FORM_LABELS.PRODUCT_ALL }, ...(mappedOptions || [])];
  }, [managedUser]);

  // ================================
  // Hook API
  // ================================

  /**
   * Return all derived values. Properties are listed alphabetically to match
   * the interface declaration and make diffing easier.
   */
  return {
    certifications,
    managedUserFullName,
    practicesCertificates,
    productOptions,
    products,
    roleplays,
    tabs,
  };
};
